import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type {
  AppController,
  AppSnapshot,
  DiaryEntry,
  ExecuteAction,
  FoodDraft,
  FoodLogQuantity,
  FoodMeasure,
  FoodPhotoEstimate,
  FoodSearchResult,
  MealName,
  NutrientAmount,
  NutritionSummary,
} from "../types";
import { MEAL_OPTIONS, mealLabel } from "../meals";
import {
  normalizeNutrientUnit,
} from "../../domain/nutrition/nutrients";
import {
  getNutrientDefinition,
  NUTRIENT_CATALOG,
  type NutrientId,
} from "../../domain/nutrition/nutrient-catalog";
import {
  FOOD_BARCODE_MAX_LENGTH,
  FOOD_BRAND_MAX_LENGTH,
  FOOD_NAME_MAX_LENGTH,
  FOOD_SERVING_NAME_MAX_LENGTH,
} from "../../domain/foods/food";
import { RecipeEditor } from "./RecipeEditor";
import { RecipeLibrary } from "./RecipeLibrary";
import { DiaryEntryEditor } from "./DiaryEntryEditor";
import { TrashIcon } from "../components/TrashIcon";

interface FoodViewProps {
  snapshot: AppSnapshot;
  controller: AppController;
  execute: ExecuteAction;
  initialDate?: string;
  initialMeal?: MealName;
  onBack: () => void;
}

const EMPTY_DRAFT: FoodDraft = {
  name: "",
  brand: "",
  barcode: "",
  basisAmount: 100,
  basisUnit: "g",
  proteinGrams: 0,
  carbohydrateGrams: 0,
  fatGrams: 0,
  fibreGrams: 0,
  micronutrients: [],
};

type FoodMeasureUnit = "g" | "ml" | "serving";
type QuantityKind = "amount" | "calories";

interface QuantityInputState {
  readonly kind: QuantityKind;
  readonly value: string;
  readonly measureId?: string;
}

interface QuantityBasis {
  readonly amount: number;
  readonly unit: FoodMeasureUnit;
  readonly servingName?: string;
  readonly measures?: readonly FoodMeasure[];
  readonly nutrition: NutritionSummary;
}

export function FoodView({ snapshot, controller, execute, initialDate, initialMeal, onBack }: FoodViewProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FoodSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [meal, setMeal] = useState<MealName>(initialMeal ?? "Breakfast");
  const [selectedDate, setSelectedDate] = useState(() =>
    initialDate && getAllowedDays(snapshot).some((day) => day.date === initialDate)
      ? initialDate
      : defaultLogDate(snapshot),
  );
  const [editingDraft, setEditingDraft] = useState<FoodDraft | null>(null);
  const [photoEstimate, setPhotoEstimate] = useState<FoodPhotoEstimate>();
  const [editingRecipe, setEditingRecipe] = useState(false);
  const [reviewingRecipes, setReviewingRecipes] = useState(false);
  const [draftMode, setDraftMode] = useState<"custom" | "import" | "photo">("custom");
  const [selectedFood, setSelectedFood] = useState<FoodSearchResult>();
  const [addingId, setAddingId] = useState<string>();
  const [isRecognizingPhoto, setIsRecognizingPhoto] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const photoRecognitionSequence = useRef(0);
  const photoRecognitionRequest = useRef<AbortController | undefined>(undefined);
  const preparationRequest = useRef(0);
  const [deletingEntryId, setDeletingEntryId] = useState<string>();
  const [editingEntry, setEditingEntry] = useState<DiaryEntry>();

  const allowedDays = useMemo(() => getAllowedDays(snapshot), [snapshot]);
  const loggingDisabled = allowedDays.length === 0;
  const selectedDay = snapshot.week.days.find((day) => day.date === selectedDate);
  const hasOpenFoodFactsResults = results.some((result) => result.sourceLabel.toLocaleLowerCase().includes("open food facts"));

  useEffect(() => () => {
    photoRecognitionSequence.current += 1;
    photoRecognitionRequest.current?.abort();
    photoRecognitionRequest.current = undefined;
  }, []);

  function cancelPhotoRecognition() {
    photoRecognitionSequence.current += 1;
    photoRecognitionRequest.current?.abort();
    photoRecognitionRequest.current = undefined;
    setIsRecognizingPhoto(false);
  }

  function handleBack() {
    cancelPhotoRecognition();
    onBack();
  }

  async function handleSearch(event: FormEvent) {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    cancelPhotoRecognition();
    setHasSearched(false);
    setIsSearching(true);
    const found = await execute(() => controller.searchFoods(trimmed));
    if (found.ok) {
      setResults(found.value);
      setHasSearched(true);
    }
    setIsSearching(false);
  }

  async function handleScan() {
    cancelPhotoRecognition();
    const found = await execute(() => controller.scanBarcode(), {
      errorMessage: "The barcode could not be scanned. You can search or enter the food manually.",
    });
    if (!found.ok || !found.value) return;
    if (found.value.requiresMacroReview) {
      setPhotoEstimate(undefined);
      setDraftMode("import");
      setEditingDraft(toDraft(found.value));
    } else {
      setResults([found.value]);
    }
  }

  async function handlePhotoSelected(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget;
    const photo = input.files?.[0];
    input.value = "";
    if (!photo) return;

    photoRecognitionRequest.current?.abort();
    const request = ++photoRecognitionSequence.current;
    const abortController = new AbortController();
    photoRecognitionRequest.current = abortController;
    setIsRecognizingPhoto(true);
    const identified = await execute(async () => {
      try {
        return await controller.recognizeFoodPhoto(photo, abortController.signal);
      } catch (error) {
        if (abortController.signal.aborted || isAbortError(error)) return undefined;
        throw error;
      }
    });
    if (request !== photoRecognitionSequence.current || abortController.signal.aborted) return;
    photoRecognitionRequest.current = undefined;
    setIsRecognizingPhoto(false);
    if (!identified.ok || !identified.value) return;

    setPhotoEstimate(identified.value);
    setDraftMode("photo");
    setEditingDraft(structuredCloneFoodDraft(identified.value.draft));
  }

  async function addFood(result: FoodSearchResult) {
    cancelPhotoRecognition();
    const request = ++preparationRequest.current;
    setAddingId(result.id);
    const prepared = await execute(() => controller.prepareFoodForAdding(result));
    if (request !== preparationRequest.current) return;
    setAddingId(undefined);
    if (!prepared.ok) return;
    const resolved = { ...prepared.value, sourceLabel: result.sourceLabel };
    setResults((current) => current.map((candidate) => candidate.id === result.id ? resolved : candidate));
    if (resolved.requiresMacroReview) {
      setPhotoEstimate(undefined);
      setDraftMode("import");
      setEditingDraft(toDraft(resolved));
      return;
    }
    setSelectedFood(resolved);
  }

  async function confirmAddFood(result: FoodSearchResult, quantity: FoodLogQuantity) {
    setAddingId(result.id);
    const logged = await execute(
      () => result.kind === "recipe"
        ? controller.logRecipe({ date: selectedDate, meal, recipeId: result.id, quantity })
        : controller.logFood({ date: selectedDate, meal, foodId: result.id, quantity }),
      { successMessage: `${result.name} added to ${mealLabel(meal).toLowerCase()}.` },
    );
    setAddingId(undefined);
    if (logged.ok) setSelectedFood(undefined);
  }

  function startCustomFood() {
    cancelPhotoRecognition();
    setEditingRecipe(false);
    setReviewingRecipes(false);
    setPhotoEstimate(undefined);
    setDraftMode("custom");
    setEditingDraft(structuredCloneFoodDraft(EMPTY_DRAFT));
  }

  function startRecipe() {
    cancelPhotoRecognition();
    setEditingDraft(null);
    setPhotoEstimate(undefined);
    setReviewingRecipes(false);
    setEditingRecipe(true);
  }

  function finishRecipe(recipe: FoodSearchResult) {
    setEditingRecipe(false);
    setResults((current) => [recipe, ...current.filter((result) => result.id !== recipe.id)]);
    setSelectedFood(recipe);
  }

  async function saveDraft(draft: FoodDraft, quantity: FoodLogQuantity) {
    if (draftMode === "photo") {
      const completed = await execute(
        () => controller.savePhotoFoodAndLog({ draft, date: selectedDate, meal, quantity }),
        { successMessage: `${draft.name} saved and added to ${mealLabel(meal).toLowerCase()}.` },
      );
      if (completed.ok) {
        setEditingDraft(null);
        setPhotoEstimate(undefined);
      }
      return;
    }

    const saved = await execute(
      () => draftMode === "import" ? controller.confirmImportedFood(draft) : controller.saveCustomFood(draft),
      { successMessage: `${draft.name} saved to your foods.` },
    );
    if (!saved.ok) return;
    if (draft.importId) {
      setResults((current) => current.map((result) => result.id === draft.importId ? saved.value : result));
    }
    await execute(
      () => controller.logFood({ date: selectedDate, meal, foodId: saved.value.id, quantity }),
      { successMessage: `${saved.value.name} saved and added to ${mealLabel(meal).toLowerCase()}.` },
    );
    setEditingDraft(null);
    setPhotoEstimate(undefined);
  }

  function closeFoodEditor() {
    setEditingDraft(null);
    setPhotoEstimate(undefined);
  }

  async function removeEntry(entryId: string, foodName: string) {
    if (!window.confirm(`Remove ${foodName} from this day?`)) return;
    setDeletingEntryId(entryId);
    await execute(() => controller.deleteDiaryEntry(entryId), { successMessage: `${foodName} removed.` });
    setDeletingEntryId(undefined);
  }

  return (
    <section className="page-section" aria-labelledby="food-heading">
      <button className="text-button food-view__back" type="button" onClick={handleBack}>
        <span aria-hidden="true">←</span> Back to diary
      </button>
      <header className="page-heading">
        <div><p className="eyebrow">Diary</p><h1 id="food-heading">Add food</h1></div>
        <div className="button-row">
          <button className="button button--ghost button--small" type="button" onClick={startCustomFood} disabled={loggingDisabled || isRecognizingPhoto}>
            Create food
          </button>
          <button className="button button--secondary button--small" type="button" onClick={startRecipe} disabled={loggingDisabled || isRecognizingPhoto}>
            Create recipe
          </button>
          <button className="button button--ghost button--small" type="button" onClick={() => {
            cancelPhotoRecognition();
            setReviewingRecipes(true);
          }} disabled={isRecognizingPhoto}>
            Review recipes
          </button>
        </div>
      </header>

      {snapshot.checkIn.isLoggingLocked && (
        <div className="notice notice--warning" role="status">
          <div>
            <strong>New logging is paused</strong>
            <p>You can still backfill the closing week using the available dates below.</p>
          </div>
        </div>
      )}

      <div className="card compact-form-grid" aria-label="Where to add this food">
        <label className="field">
          <span>Date</span>
          <select value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} disabled={loggingDisabled}>
            {allowedDays.map((day) => <option key={day.date} value={day.date}>{day.longLabel}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Meal</span>
          <select value={meal} onChange={(event) => setMeal(event.target.value as MealName)} disabled={loggingDisabled}>
            {MEAL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>

      {selectedDay && selectedDay.diaryEntries.length > 0 && (
        <article className="card">
          <div className="card__heading"><div><h2>Entries on this date</h2><p>Review or correct the selected day before check-in.</p></div></div>
          <ul className="entry-list">
            {selectedDay.diaryEntries.map((entry) => (
              <li className="entry-row" key={entry.id}>
                <button
                  className="entry-row__edit-card"
                  type="button"
                  onClick={() => setEditingEntry(entry)}
                  aria-label={`Edit ${entry.foodName} in ${mealLabel(entry.meal)}`}
                >
                  <span className="entry-row__content">
                    <strong>{entry.foodName}</strong>
                    <span>{mealLabel(entry.meal)} · {entry.amountLabel} · {Math.round(entry.nutrition.calories)} kcal</span>
                  </span>
                </button>
                <button
                  className="icon-button icon-button--danger entry-row__delete"
                  type="button"
                  disabled={deletingEntryId === entry.id}
                  onClick={() => removeEntry(entry.id, entry.foodName)}
                  aria-label={`Delete ${entry.foodName} from ${mealLabel(entry.meal)}`}
                  title={`Delete ${entry.foodName}`}
                ><TrashIcon /></button>
              </li>
            ))}
          </ul>
        </article>
      )}

      <article className="card">
        <form className="search-row" onSubmit={handleSearch}>
          <label className="field search-row__field">
            <span className="sr-only">Food name or barcode</span>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setResults([]);
                setHasSearched(false);
              }}
              placeholder="Search food or enter a barcode"
              autoComplete="off"
              disabled={loggingDisabled || isRecognizingPhoto}
            />
          </label>
          <button className="button button--primary" type="submit" disabled={loggingDisabled || isSearching || isRecognizingPhoto || !query.trim()}>
            {isSearching ? "Searching…" : "Search"}
          </button>
        </form>
        <div className="camera-actions">
          <button
            className="button button--camera"
            type="button"
            onClick={() => photoInput.current?.click()}
            disabled={loggingDisabled || !snapshot.isOnline || isRecognizingPhoto}
            aria-describedby="photo-recognition-privacy"
          >
            <span aria-hidden="true">◉</span> {isRecognizingPhoto ? "Identifying food…" : "Identify food with camera"}
          </button>
          <input
            ref={photoInput}
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(event) => void handlePhotoSelected(event)}
          />
          <button className="button button--scan" type="button" onClick={handleScan} disabled={loggingDisabled || isRecognizingPhoto}>
            <span aria-hidden="true">▣</span> Scan a barcode
          </button>
        </div>
        <p className="field-hint" id="photo-recognition-privacy">
          A photo estimate must be checked before it is saved. The resized photo is sent to OpenAI for analysis, but this app does not put it in your diary or backups.
        </p>
        {isRecognizingPhoto && <p className="sr-only" role="status">Identifying the food in your photo.</p>}
        {!snapshot.isOnline && <p className="field-hint">Barcode scanning can still find foods saved on this device. Public food search, online barcode lookup, and photo recognition need an internet connection.</p>}
      </article>

      {results.length > 0 && (
        <section aria-labelledby="results-heading">
          <div className="section-heading"><h2 id="results-heading">Results</h2><span>{results.length} found</span></div>
          <ul className="food-results">
            {results.map((result) => (
              <li className="food-result" key={result.id}>
                <div className="food-result__main">
                  <div>
                    <strong>{result.name}</strong>
                    <span>{[result.brand, result.basisLabel, result.sourceLabel].filter(Boolean).join(" · ")}</span>
                    {result.kind === "recipe" && result.ingredientCount !== undefined && (
                      <span>{result.ingredientCount} {result.ingredientCount === 1 ? "ingredient" : "ingredients"}</span>
                    )}
                  </div>
                  <strong className="food-result__calories">{Math.round(result.nutrition.calories)} kcal</strong>
                </div>
                <div className="macro-summary" aria-label="Macronutrients">
                  <span>P {formatGrams(result.nutrition.proteinGrams)}</span>
                  <span>C {formatGrams(result.nutrition.carbohydrateGrams)}</span>
                  <span>F {formatGrams(result.nutrition.fatGrams)}</span>
                </div>
                {result.requiresMacroReview && <p className="inline-warning">Macros must be checked before this food can be logged.</p>}
                <button
                  className="button button--secondary button--full"
                  type="button"
                  onClick={() => void addFood(result)}
                  disabled={addingId !== undefined || isRecognizingPhoto}
                >
                  {addingId === result.id ? "Adding…" : result.requiresMacroReview ? "Review macros" : `Add to ${mealLabel(meal)}`}
                </button>
              </li>
            ))}
          </ul>
          {hasOpenFoodFactsResults && (
            <p className="food-attribution">
              Food data provided by <a href="https://world.openfoodfacts.org/">Open Food Facts</a>, a collaborative public food database.
            </p>
          )}
        </section>
      )}

      {hasSearched && !isSearching && results.length === 0 && (
        <div className="empty-state">
          <strong>No results to show</strong>
          <p>Try another description, scan the package, or create your own food or recipe.</p>
          <div className="button-row">
            <button className="button button--ghost" type="button" onClick={startCustomFood}>Create this food</button>
            <button className="button button--secondary" type="button" onClick={startRecipe}>Create a recipe</button>
          </div>
        </div>
      )}

      {editingDraft && (
        <FoodEditor
          initialDraft={editingDraft}
          mode={draftMode}
          recognition={photoEstimate}
          onCancel={closeFoodEditor}
          onSave={saveDraft}
        />
      )}

      {editingRecipe && (
        <RecipeEditor
          controller={controller}
          execute={execute}
          onCancel={() => setEditingRecipe(false)}
          onSaved={finishRecipe}
        />
      )}

      {reviewingRecipes && (
        <RecipeLibrary
          controller={controller}
          execute={execute}
          onCancel={() => setReviewingRecipes(false)}
        />
      )}

      {editingEntry && (
        <DiaryEntryEditor
          snapshot={snapshot}
          entry={editingEntry}
          controller={controller}
          execute={execute}
          onCancel={() => setEditingEntry(undefined)}
          onSaved={() => setEditingEntry(undefined)}
        />
      )}

      {selectedFood && (
        <AddFoodDialog
          key={selectedFood.id}
          result={selectedFood}
          meal={meal}
          isAdding={addingId === selectedFood.id}
          onCancel={() => setSelectedFood(undefined)}
          onConfirm={(quantity) => confirmAddFood(selectedFood, quantity)}
        />
      )}
    </section>
  );
}

function AddFoodDialog({
  result,
  meal,
  isAdding,
  onCancel,
  onConfirm,
}: {
  result: FoodSearchResult;
  meal: MealName;
  isAdding: boolean;
  onCancel: () => void;
  onConfirm: (quantity: FoodLogQuantity) => Promise<void>;
}) {
  const basis = quantityBasisFromResult(result);
  const [quantity, setQuantity] = useState<QuantityInputState>(() => defaultQuantity(basis));
  const preview = calculateQuantityPreview(basis, quantity);
  const logQuantity = quantityForLogging(basis, quantity);
  const { dialogRef, handleDialogKeyDown } = useModalDialog(onCancel, isAdding);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!preview || !logQuantity || isAdding) return;
    await onConfirm(logQuantity);
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-sheet modal-sheet--quantity"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-food-dialog-heading"
        onKeyDown={handleDialogKeyDown}
      >
        <div className="modal-sheet__handle" aria-hidden="true" />
        <div className="modal-sheet__heading">
          <div>
            <p className="eyebrow">{mealLabel(meal)}</p>
            <h2 id="add-food-dialog-heading">Add {result.name}</h2>
            <p className="quantity-dialog__basis">{result.basisLabel} · {formatCalories(basis.nutrition.calories)} kcal</p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Close add food dialog" disabled={isAdding}>x</button>
        </div>

        <form className="stack" onSubmit={submit}>
          <QuantityPicker
            idPrefix="add-food"
            basis={basis}
            quantity={quantity}
            onChange={setQuantity}
          />
          <div className="modal-actions">
            <button className="button button--ghost" type="button" onClick={onCancel} disabled={isAdding}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={isAdding || !preview || !logQuantity}>
              {isAdding ? "Adding…" : `Add to ${mealLabel(meal)}`}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function FoodEditor({
  initialDraft,
  mode,
  recognition,
  onCancel,
  onSave,
}: {
  initialDraft: FoodDraft;
  mode: "custom" | "import" | "photo";
  recognition?: FoodPhotoEstimate;
  onCancel: () => void;
  onSave: (draft: FoodDraft, quantity: FoodLogQuantity) => Promise<void>;
}) {
  const [draft, setDraft] = useState(() => lockDraftNutrientsToCatalog(initialDraft));
  const [quantity, setQuantity] = useState<QuantityInputState>(() => defaultQuantity(quantityBasisFromDraft(initialDraft)));
  const [confirmed, setConfirmed] = useState(mode === "custom");
  const [saving, setSaving] = useState(false);
  const [nutrientError, setNutrientError] = useState<string>();
  const nextNutrientId = useRef(1);
  const { dialogRef, handleDialogKeyDown } = useModalDialog(onCancel, saving);
  const derivedCalories = draft.proteinGrams * 4 + draft.carbohydrateGrams * 4 + draft.fatGrams * 9;
  const basisAmountValid = Number.isFinite(draft.basisAmount) && draft.basisAmount > 0;
  const quantityBasis = quantityBasisFromDraft(draft);
  const quantityPreview = calculateQuantityPreview(quantityBasis, quantity);
  const logQuantity = quantityForLogging(quantityBasis, quantity);
  const importedMacrosChanged =
    draft.proteinGrams !== initialDraft.proteinGrams ||
    draft.carbohydrateGrams !== initialDraft.carbohydrateGrams ||
    draft.fatGrams !== initialDraft.fatGrams;
  const selectedNutrientIds = new Set(draft.micronutrients.map((nutrient) => nutrient.id));
  const availableNutrients = NUTRIENT_CATALOG.filter((definition) => !selectedNutrientIds.has(definition.id));
  const hasPendingNutrient = draft.micronutrients.some((nutrient) => !getNutrientDefinition(nutrient.id));
  const photoWarningId = mode === "photo" && recognition ? "photo-estimate-warning" : undefined;

  useEffect(() => {
    if (derivedCalories <= 0 && quantity.kind === "calories") {
      setQuantity({ kind: "amount", value: formatQuantityInput(draft.basisAmount) });
    }
  }, [derivedCalories, draft.basisAmount, quantity.kind]);

  function setNumber(key: "basisAmount" | "proteinGrams" | "carbohydrateGrams" | "fatGrams" | "fibreGrams", value: string) {
    const nextValue = Math.max(0, Number(value) || 0);
    if (key === "basisAmount" && nextValue > 0) {
      setQuantity((current) => current.kind === "amount" && Number(current.value) === draft.basisAmount
        ? { ...current, value: formatQuantityInput(nextValue) }
        : current);
    }
    setDraft((current) => ({ ...current, [key]: nextValue }));
  }

  function setBasisUnit(unit: FoodDraft["basisUnit"]) {
    const currentPreview = calculateQuantityPreview(quantityBasis, quantity);
    setDraft((current) => ({ ...current, basisUnit: unit, measures: undefined }));
    if (quantity.kind === "amount") {
      setQuantity({ kind: "amount", value: formatConvertedQuantityInput(currentPreview?.amount ?? draft.basisAmount) });
    } else {
      setQuantity({ kind: "calories", value: quantity.value });
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (
      !draft.name.trim() ||
      !quantityPreview ||
      !logQuantity ||
      (mode === "import" && (!confirmed || !importedMacrosChanged)) ||
      (mode === "photo" && !confirmed)
    ) return;
    const micronutrients = normalizeDraftMicronutrients(draft.micronutrients);
    if (!micronutrients.ok) {
      setNutrientError(micronutrients.message);
      return;
    }
    setNutrientError(undefined);
    setSaving(true);
    await onSave(
      {
        ...draft,
        name: draft.name.trim(),
        brand: draft.brand?.trim(),
        micronutrients: micronutrients.value,
      },
      logQuantity,
    );
    setSaving(false);
  }

  function updateMicronutrient(index: number, changes: Partial<NutrientAmount>) {
    const micronutrients = draft.micronutrients.map((nutrient, nutrientIndex) =>
      nutrientIndex === index ? { ...nutrient, ...changes } : nutrient,
    );
    setDraft((current) => ({ ...current, micronutrients }));
    setNutrientError(undefined);
  }

  function addMicronutrient() {
    if (availableNutrients.length === 0 || hasPendingNutrient) return;
    let id = `new-nutrient-${nextNutrientId.current++}`;
    while (draft.micronutrients.some((nutrient) => nutrient.id === id)) {
      id = `new-nutrient-${nextNutrientId.current++}`;
    }
    setDraft((current) => ({
      ...current,
      micronutrients: [...current.micronutrients, {
        id,
        name: "",
        amount: 0,
        unit: "mg",
      }],
    }));
    setNutrientError(undefined);
  }

  function selectMicronutrient(index: number, nutrientId: NutrientId) {
    const definition = getNutrientDefinition(nutrientId);
    if (!definition) return;
    updateMicronutrient(index, {
      id: definition.id,
      name: definition.name,
      amount: 0,
      unit: definition.unit,
    });
  }

  function removeMicronutrient(index: number) {
    setDraft((current) => ({
      ...current,
      micronutrients: current.micronutrients.filter((_, nutrientIndex) => nutrientIndex !== index),
    }));
    setNutrientError(undefined);
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section ref={dialogRef} className="modal-sheet" role="dialog" aria-modal="true" aria-labelledby="food-editor-heading" aria-describedby={photoWarningId} onKeyDown={handleDialogKeyDown}>
        <div className="modal-sheet__handle" aria-hidden="true" />
        <div className="modal-sheet__heading">
          <div>
            <p className="eyebrow">{mode === "import" ? "Before importing" : mode === "photo" ? "Photo estimate" : "Your foods"}</p>
            <h2 id="food-editor-heading">{mode === "import" ? "Check the macros" : mode === "photo" ? "Review food estimate" : "Create a food"}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Close food editor" disabled={saving}>x</button>
        </div>

        {mode === "import" && (
          <div className="notice notice--warning">
            <div>
              <strong>The source did not provide complete macros</strong>
              <p>Its reported calories were split into an editable starting point. Correct the protein, carbohydrate and fat from the package before saving.</p>
            </div>
          </div>
        )}

        {mode === "photo" && recognition && (
          <div className="notice notice--warning photo-estimate" id={photoWarningId}>
            <div>
              <strong>Check this {recognition.confidence}-confidence estimate</strong>
              <p>{recognition.description} Portion size and nutrition cannot be measured exactly from a photo.</p>
              {recognition.assumptions.length > 0 && (
                <ul className="photo-estimate__assumptions">
                  {recognition.assumptions.map((assumption) => <li key={assumption}>{assumption}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}

        <form className="stack" onSubmit={submit}>
          <div className="form-grid">
            <label className="field field--wide"><span>Food name</span><input required maxLength={FOOD_NAME_MAX_LENGTH} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
            <label className="field"><span>Brand <em>optional</em></span><input maxLength={FOOD_BRAND_MAX_LENGTH} value={draft.brand ?? ""} onChange={(event) => setDraft({ ...draft, brand: event.target.value })} /></label>
            <label className="field"><span>Barcode <em>optional</em></span><input inputMode="numeric" maxLength={FOOD_BARCODE_MAX_LENGTH} value={draft.barcode ?? ""} onChange={(event) => setDraft({ ...draft, barcode: event.target.value })} /></label>
          </div>

          <fieldset className="fieldset">
            <legend>Nutrition basis</legend>
            <div className="inline-fields">
              <label className="field">
                <span>Amount</span>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={draft.basisAmount}
                  aria-invalid={!basisAmountValid}
                  aria-describedby={!basisAmountValid ? "food-basis-amount-error" : undefined}
                  onChange={(event) => setNumber("basisAmount", event.target.value)}
                />
                {!basisAmountValid && <small className="field-error" id="food-basis-amount-error" role="alert">Enter a nutrition basis greater than zero.</small>}
              </label>
              <label className="field"><span>Unit</span><select value={draft.basisUnit} onChange={(event) => setBasisUnit(event.target.value as FoodDraft["basisUnit"])}><option value="g">grams</option><option value="ml">millilitres</option><option value="serving">serving</option></select></label>
            </div>
            {draft.basisUnit === "serving" && <label className="field"><span>Serving name</span><input required maxLength={FOOD_SERVING_NAME_MAX_LENGTH} placeholder="e.g. one bar" value={draft.servingName ?? ""} onChange={(event) => setDraft({ ...draft, servingName: event.target.value })} /></label>}
          </fieldset>

          <fieldset className="fieldset">
            <legend>Macronutrients and fibre</legend>
            <div className="macro-inputs">
              <NumberField label="Protein" value={draft.proteinGrams} suffix="g" onChange={(value) => setNumber("proteinGrams", value)} />
              <NumberField label="Carbohydrate" value={draft.carbohydrateGrams} suffix="g" onChange={(value) => setNumber("carbohydrateGrams", value)} />
              <NumberField label="Fat" value={draft.fatGrams} suffix="g" onChange={(value) => setNumber("fatGrams", value)} />
              <NumberField label="Fibre" value={draft.fibreGrams} suffix="g" onChange={(value) => setNumber("fibreGrams", value)} />
            </div>
            <div className="derived-calories">
              <span>Calories derived from 4 / 4 / 9</span>
              <strong>{Math.round(derivedCalories)} kcal</strong>
            </div>
            {draft.providerCalories !== undefined && (
              <p className="field-hint">The source reported {Math.round(draft.providerCalories)} kcal. Small label differences can occur from rounding and non-standard energy components.</p>
            )}
          </fieldset>

          <QuantityPicker
            idPrefix="food-editor"
            basis={quantityBasis}
            quantity={quantity}
            onChange={setQuantity}
          />

          <details className="details-panel">
            <summary>Vitamins and minerals <span>optional</span></summary>
            <div className="details-panel__content stack stack--small">
              <p className="field-hint">Fibre is entered separately above under Macronutrients and fibre.</p>
              {draft.micronutrients.length === 0 && (
                <p className="field-hint">Add only the nutrients shown on the label or important to you.</p>
              )}
              <ul className="nutrient-editor-list">
                {draft.micronutrients.map((nutrient, index) => {
                  const definition = getNutrientDefinition(nutrient.id);
                  const accessibleName = definition?.name ?? `nutrient ${index + 1}`;
                  const selectableNutrients = NUTRIENT_CATALOG.filter((candidate) =>
                    candidate.id === nutrient.id || !selectedNutrientIds.has(candidate.id),
                  );
                  return (
                    <li key={nutrient.id} className="nutrient-editor-row">
                      <label className="field nutrient-editor-row__name">
                        <span>Vitamin or mineral</span>
                        <select
                          required
                          value={definition?.id ?? ""}
                          aria-label={`Nutrient ${index + 1} name`}
                          onChange={(event) => selectMicronutrient(index, event.target.value as NutrientId)}
                        >
                          {!definition && <option value="">Choose a vitamin or mineral</option>}
                          <NutrientCatalogOptionGroups definitions={selectableNutrients} />
                        </select>
                      </label>
                      <label className="field">
                        <span>Amount</span>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          required
                          value={nutrient.amount}
                          aria-label={`${accessibleName} amount`}
                          onChange={(event) => updateMicronutrient(index, { amount: Math.max(0, Number(event.target.value) || 0) })}
                        />
                      </label>
                      <label className="field">
                        <span>Unit</span>
                        <output className="read-only-field" aria-label={`${accessibleName} unit`}>
                          <strong>{definition?.unit ?? normalizeNutrientUnit(nutrient.unit)}</strong>
                        </output>
                      </label>
                      <button
                        className="icon-button icon-button--danger nutrient-editor-row__remove"
                        type="button"
                        aria-label={`Remove ${accessibleName}`}
                        onClick={() => removeMicronutrient(index)}
                      >x</button>
                    </li>
                  );
                })}
              </ul>
              {nutrientError && <p className="form-error" role="alert">{nutrientError}</p>}
              <button className="button button--secondary" type="button" onClick={addMicronutrient} disabled={availableNutrients.length === 0 || hasPendingNutrient}>
                {availableNutrients.length === 0
                  ? "All vitamins and minerals added"
                  : hasPendingNutrient
                    ? "Choose the vitamin or mineral above"
                    : "Add vitamin or mineral"}
              </button>
            </div>
          </details>

          {mode === "import" && (
            <div className="stack stack--small">
              {!importedMacrosChanged && <p className="form-error">Change the fallback protein, carbohydrate or fat values to match the food label.</p>}
              <label className="check-field">
                <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                <span>I have checked and corrected all three macronutrients.</span>
              </label>
            </div>
          )}

          {mode === "photo" && (
            <label className="check-field">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
              <span>I have checked the food name, estimated portion and all three macronutrients.</span>
            </label>
          )}

          <div className="modal-actions">
            <button className="button button--ghost" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={saving || !draft.name.trim() || !confirmed || !quantityPreview || !logQuantity || (mode === "import" && !importedMacrosChanged)}>
              {saving ? "Saving…" : "Save and add"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function QuantityPicker({
  idPrefix,
  basis,
  quantity,
  onChange,
}: {
  idPrefix: string;
  basis: QuantityBasis;
  quantity: QuantityInputState;
  onChange: (quantity: QuantityInputState) => void;
}) {
  const caloriesAvailable = Number.isFinite(basis.nutrition.calories) && basis.nutrition.calories > 0;
  const preview = calculateQuantityPreview(basis, quantity);
  const selectedMeasure = selectedFoodMeasure(basis, quantity.measureId);
  const nativeOptionLabel = basis.measures && basis.measures.length > 0
    ? "Amount"
    : nativeQuantityOptionLabel(basis.unit, basis.servingName);
  const inputLabel = quantity.kind === "calories"
    ? "Calories to add"
    : selectedMeasure
      ? `Quantity in ${measureInputName(selectedMeasure)}`
      : nativeQuantityInputLabel(basis.unit, basis.servingName);
  const suffix = quantity.kind === "calories"
    ? "kcal"
    : selectedMeasure?.id === "package"
      ? "packages"
      : selectedMeasure?.id === "serving"
        ? "servings"
        : selectedMeasure?.label ?? nativeQuantitySuffix(basis.unit, basis.servingName);
  const helpId = `${idPrefix}-quantity-help`;
  const calorieHelpId = `${idPrefix}-calorie-help`;
  const quantityErrorId = `${idPrefix}-quantity-error`;
  const quantityValue = Number(quantity.value);
  const quantityValid = quantity.value.trim().length > 0 && Number.isFinite(quantityValue) && quantityValue > 0;
  const basisValid = Number.isFinite(basis.amount) && basis.amount > 0;

  function chooseKind(kind: QuantityKind) {
    if (kind === quantity.kind || (kind === "calories" && !caloriesAvailable)) return;
    const currentPreview = calculateQuantityPreview(basis, quantity);
    const nextValue = kind === "amount"
      ? amountInSelectedMeasure(currentPreview?.amount ?? basis.amount, selectedMeasure)
      : currentPreview?.calories ?? basis.nutrition.calories;
    onChange({ kind, value: formatConvertedQuantityInput(nextValue), ...(quantity.measureId
      ? { measureId: quantity.measureId }
      : {}) });
  }

  function chooseMeasure(measureId: string) {
    const currentPreview = calculateQuantityPreview(basis, quantity);
    const nextMeasure = selectedFoodMeasure(basis, measureId || undefined);
    const canonicalAmount = currentPreview?.amount ?? basis.amount;
    onChange({
      kind: "amount",
      value: formatConvertedQuantityInput(amountInSelectedMeasure(canonicalAmount, nextMeasure)),
      ...(nextMeasure === undefined ? {} : { measureId: nextMeasure.id }),
    });
  }

  return (
    <fieldset className="fieldset quantity-picker">
      <legend>Amount to add</legend>
      <div className="segmented-control segmented-control--two quantity-picker__modes">
        <label className={quantity.kind === "amount" ? "segmented-control__item segmented-control__item--selected" : "segmented-control__item"}>
          <input
            type="radio"
            name={`${idPrefix}-quantity-kind`}
            checked={quantity.kind === "amount"}
            onChange={() => chooseKind("amount")}
          />
          <span title={nativeOptionLabel}>{nativeOptionLabel}</span>
        </label>
        <label className={`segmented-control__item${quantity.kind === "calories" ? " segmented-control__item--selected" : ""}${caloriesAvailable ? "" : " segmented-control__item--disabled"}`}>
          <input
            type="radio"
            name={`${idPrefix}-quantity-kind`}
            checked={quantity.kind === "calories"}
            onChange={() => chooseKind("calories")}
            disabled={!caloriesAvailable}
            aria-describedby={!caloriesAvailable ? calorieHelpId : undefined}
          />
          <span>Calories</span>
        </label>
      </div>

      {!caloriesAvailable && <p className="field-hint" id={calorieHelpId}>This food has no calories, so enter its amount instead.</p>}

      {quantity.kind === "amount" && basis.measures && basis.measures.length > 0 && (
        <label className="field">
          <span>Unit</span>
          <select
            value={quantity.measureId ?? ""}
            aria-label="Food unit"
            onChange={(event) => chooseMeasure(event.target.value)}
          >
            <option value="">{nativeQuantityOptionLabel(basis.unit, basis.servingName)}</option>
            {basis.measures.map((measure) => (
              <option key={measure.id} value={measure.id}>{measureOptionLabel(measure, basis)}</option>
            ))}
          </select>
        </label>
      )}

      <label className="field" htmlFor={`${idPrefix}-quantity-value`}>
        <span>{inputLabel}</span>
        <span className="input-with-suffix input-with-suffix--large">
          <input
            id={`${idPrefix}-quantity-value`}
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            required
            value={quantity.value}
            onChange={(event) => onChange({ ...quantity, value: event.target.value })}
            aria-label={inputLabel}
            aria-invalid={!quantityValid}
            aria-describedby={`${helpId}${quantityValid ? "" : ` ${quantityErrorId}`}`}
          />
          <i>{suffix}</i>
        </span>
        <small id={helpId}>Enter the amount you actually ate. Nutrition updates automatically.</small>
      </label>

      {!quantityValid ? (
        <p className="form-error" id={quantityErrorId} role="alert">Enter a quantity greater than zero.</p>
      ) : preview ? (
        <div className="quantity-preview" role="status" aria-live="polite" aria-label="Quantity preview">
          <dl className="quantity-preview__totals">
            <div><dt>Amount</dt><dd>{formatSelectedAmount(
              preview.amount,
              quantityValue,
              basis,
              quantity.kind === "amount" ? selectedMeasure : undefined,
            )}</dd></div>
            <div><dt>Calories</dt><dd>{formatCalories(preview.calories)} kcal</dd></div>
          </dl>
          <div className="macro-summary quantity-preview__macros" aria-label="Nutrients for this quantity">
            <span>P {formatGrams(preview.proteinGrams)}</span>
            <span>C {formatGrams(preview.carbohydrateGrams)}</span>
            <span>F {formatGrams(preview.fatGrams)}</span>
            {preview.fibreGrams !== undefined && <span>Fibre {formatGrams(preview.fibreGrams)}</span>}
          </div>
        </div>
      ) : !basisValid ? (
        <p className="field-hint">Correct the nutrition basis above to preview this amount.</p>
      ) : null}
    </fieldset>
  );
}

function NumberField({ label, value, suffix, onChange }: { label: string; value: number; suffix: string; onChange: (value: string) => void }) {
  return (
    <label className="field number-field">
      <span>{label}</span>
      <span className="input-with-suffix"><input aria-label={`${label} ${suffix}`} type="number" min="0" step="0.01" value={value} onChange={(event) => onChange(event.target.value)} /><i>{suffix}</i></span>
    </label>
  );
}

function getAllowedDays(snapshot: AppSnapshot) {
  if (!snapshot.checkIn.isLoggingLocked) {
    return snapshot.week.days.filter((day) => !day.isFuture);
  }
  const allowed = new Set(snapshot.checkIn.allowedBackfillDates);
  return snapshot.week.days.filter((day) => allowed.has(day.date));
}

function defaultLogDate(snapshot: AppSnapshot) {
  const allowed = getAllowedDays(snapshot);
  return allowed.find((day) => day.isToday)?.date ?? allowed.at(-1)?.date ?? snapshot.todayDate;
}

function toDraft(result: FoodSearchResult): FoodDraft {
  return {
    importId: result.id,
    name: result.name,
    brand: result.brand,
    barcode: result.barcode,
    basisAmount: result.basisAmount,
    basisUnit: result.basisUnit,
    servingName: result.servingName,
    ...(result.measures === undefined ? {} : { measures: result.measures.map((measure) => ({ ...measure })) }),
    proteinGrams: result.nutrition.proteinGrams,
    carbohydrateGrams: result.nutrition.carbohydrateGrams,
    fatGrams: result.nutrition.fatGrams,
    fibreGrams: result.nutrition.fibreGrams ?? 0,
    micronutrients: (result.nutrition.micronutrients ?? []).map((nutrient) => ({
      ...nutrient,
      unit: normalizeNutrientUnit(nutrient.unit),
    })),
    sourceLabel: result.sourceLabel,
    providerCalories: result.providerCalories,
    requiresMacroReview: true,
  };
}

function lockDraftNutrientsToCatalog(draft: FoodDraft): FoodDraft {
  return {
    ...draft,
    ...(draft.measures === undefined ? {} : { measures: draft.measures.map((measure) => ({ ...measure })) }),
    micronutrients: draft.micronutrients.map((nutrient) => {
      const definition = getNutrientDefinition(nutrient.id);
      return definition
        ? { ...nutrient, id: definition.id, name: definition.name, unit: definition.unit }
        : { ...nutrient, unit: normalizeNutrientUnit(nutrient.unit) };
    }),
  };
}

function NutrientCatalogOptionGroups({
  definitions,
}: {
  definitions: ReadonlyArray<(typeof NUTRIENT_CATALOG)[number]>;
}) {
  return (
    <>
      <optgroup label="Vitamins">
        {definitions.filter((definition) => definition.category === "vitamin").map((definition) => (
          <option key={definition.id} value={definition.id}>{definition.name}</option>
        ))}
      </optgroup>
      <optgroup label="Minerals">
        {definitions.filter((definition) => definition.category === "mineral").map((definition) => (
          <option key={definition.id} value={definition.id}>{definition.name}</option>
        ))}
      </optgroup>
    </>
  );
}

function structuredCloneFoodDraft(draft: FoodDraft): FoodDraft {
  return {
    ...draft,
    micronutrients: draft.micronutrients.map((nutrient: NutrientAmount) => ({
      ...nutrient,
      unit: normalizeNutrientUnit(nutrient.unit),
    })),
  };
}

function quantityBasisFromResult(result: FoodSearchResult): QuantityBasis {
  return {
    amount: result.basisAmount,
    unit: result.basisUnit,
    servingName: result.servingName,
    measures: result.measures,
    nutrition: result.nutrition,
  };
}

function quantityBasisFromDraft(draft: FoodDraft): QuantityBasis {
  return {
    amount: draft.basisAmount,
    unit: draft.basisUnit,
    servingName: draft.servingName,
    measures: draft.measures,
    nutrition: {
      calories: draft.proteinGrams * 4 + draft.carbohydrateGrams * 4 + draft.fatGrams * 9,
      proteinGrams: draft.proteinGrams,
      carbohydrateGrams: draft.carbohydrateGrams,
      fatGrams: draft.fatGrams,
      fibreGrams: draft.fibreGrams,
      micronutrients: draft.micronutrients,
    },
  };
}

function defaultQuantity(basis: QuantityBasis): QuantityInputState {
  const serving = basis.measures?.find((measure) => measure.id === "serving");
  if (serving) return { kind: "amount", value: "1", measureId: serving.id };
  return { kind: "amount", value: formatQuantityInput(basis.amount > 0 ? basis.amount : 1) };
}

function calculateQuantityPreview(basis: QuantityBasis, quantity: QuantityInputState): {
  readonly amount: number;
  readonly calories: number;
  readonly proteinGrams: number;
  readonly carbohydrateGrams: number;
  readonly fatGrams: number;
  readonly fibreGrams?: number;
} | undefined {
  if (!quantity.value.trim() || !Number.isFinite(basis.amount) || basis.amount <= 0) return undefined;
  const entered = Number(quantity.value);
  if (!Number.isFinite(entered) || entered <= 0) return undefined;
  if (quantity.kind === "calories" && (!Number.isFinite(basis.nutrition.calories) || basis.nutrition.calories <= 0)) return undefined;
  const measure = selectedFoodMeasure(basis, quantity.measureId);
  const canonicalAmount = quantity.kind === "amount" ? entered * (measure?.amount ?? 1) : undefined;
  const scale = quantity.kind === "amount"
    ? canonicalAmount! / basis.amount
    : entered / basis.nutrition.calories;
  if (!Number.isFinite(scale) || scale <= 0) return undefined;
  return {
    amount: basis.amount * scale,
    calories: basis.nutrition.calories * scale,
    proteinGrams: basis.nutrition.proteinGrams * scale,
    carbohydrateGrams: basis.nutrition.carbohydrateGrams * scale,
    fatGrams: basis.nutrition.fatGrams * scale,
    ...(basis.nutrition.fibreGrams === undefined ? {} : { fibreGrams: basis.nutrition.fibreGrams * scale }),
  };
}

function quantityForLogging(basis: QuantityBasis, quantity: QuantityInputState): FoodLogQuantity | undefined {
  if (!quantity.value.trim()) return undefined;
  const value = Number(quantity.value);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  const measure = selectedFoodMeasure(basis, quantity.measureId);
  return quantity.kind === "amount"
    ? { kind: "amount", amount: value, ...(measure ? { measureId: measure.id } : {}) }
    : { kind: "calories", calories: value };
}

function nativeQuantityOptionLabel(unit: FoodMeasureUnit, servingName?: string): string {
  if (unit === "g") return "Grams";
  if (unit === "ml") return "Millilitres";
  return capitalizeFirst(servingName?.trim() || "Servings");
}

function nativeQuantityInputLabel(unit: FoodMeasureUnit, servingName?: string): string {
  if (unit === "g") return "Quantity in grams";
  if (unit === "ml") return "Quantity in millilitres";
  return `Quantity in ${servingName?.trim() || "servings"}`;
}

function nativeQuantitySuffix(unit: FoodMeasureUnit, servingName?: string): string {
  if (unit === "serving") return servingName?.trim() || "servings";
  return unit;
}

function formatNativeAmount(amount: number, unit: FoodMeasureUnit, servingName?: string): string {
  return `${formatQuantityNumber(amount)} ${nativeQuantitySuffix(unit, servingName)}`;
}

function selectedFoodMeasure(basis: QuantityBasis, measureId: string | undefined): FoodMeasure | undefined {
  if (!measureId) return undefined;
  return basis.measures?.find((measure) => measure.id === measureId);
}

function amountInSelectedMeasure(canonicalAmount: number, measure: FoodMeasure | undefined): number {
  return measure === undefined ? canonicalAmount : canonicalAmount / measure.amount;
}

function measureInputName(measure: FoodMeasure): string {
  if (measure.id === "serving") return "servings";
  if (measure.id === "package") return "packages";
  return measure.label.toLocaleLowerCase();
}

function measureOptionLabel(measure: FoodMeasure, basis: QuantityBasis): string {
  const conversion = formatNativeAmount(measure.amount, basis.unit, basis.servingName);
  return `${measure.label} · ${conversion}`;
}

function formatSelectedAmount(
  canonicalAmount: number,
  enteredAmount: number,
  basis: QuantityBasis,
  measure: FoodMeasure | undefined,
): string {
  const canonical = formatNativeAmount(canonicalAmount, basis.unit, basis.servingName);
  if (!measure) return canonical;
  const suffix = measure.id === "serving"
    ? enteredAmount === 1 ? "serving" : "servings"
    : measure.id === "package"
      ? enteredAmount === 1 ? "package" : "packages"
      : measure.label;
  return `${formatQuantityNumber(enteredAmount)} ${suffix} · ${canonical}`;
}

function formatCalories(calories: number): string {
  return formatQuantityNumber(calories);
}

function formatQuantityInput(value: number): string {
  if (!Number.isFinite(value)) return "";
  return String(value);
}

function formatConvertedQuantityInput(value: number): string {
  return Number.isFinite(value) ? String(Number(value.toPrecision(16))) : "";
}

function formatQuantityNumber(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function capitalizeFirst(value: string): string {
  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

function useModalDialog(onCancel: () => void, dismissDisabled = false) {
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const firstField = dialog?.querySelector<HTMLElement>("input:not(:disabled), select:not(:disabled), textarea:not(:disabled)");
    const firstControl = firstField ?? dialog?.querySelector<HTMLElement>("button:not(:disabled), summary");
    firstControl?.focus();
    return () => {
      previouslyFocused?.focus();
    };
  }, []);

  function handleDialogKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (!dismissDisabled) onCancel();
      return;
    }
    if (event.key !== "Tab") return;
    const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("*"))
      .filter((control) => control.matches("input:not(:disabled), button:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary"))
      .filter((control) => control.tabIndex >= 0 && !control.hidden)
      .filter((control) => control.tagName === "SUMMARY" || !control.closest("details:not([open])"));
    if (controls.length === 0) return;
    const first = controls[0];
    const last = controls.at(-1)!;
    if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return { dialogRef, handleDialogKeyDown };
}

function normalizeDraftMicronutrients(
  nutrients: readonly NutrientAmount[],
): { ok: true; value: NutrientAmount[] } | { ok: false; message: string } {
  const normalized: NutrientAmount[] = [];
  const keys = new Set<string>();
  for (const nutrient of nutrients) {
    const definition = getNutrientDefinition(nutrient.id);
    if (!definition) return { ok: false, message: "Choose a vitamin or mineral from the list, or remove the row." };
    if (keys.has(definition.id)) return { ok: false, message: `${definition.name} has been added more than once.` };
    keys.add(definition.id);
    normalized.push({
      id: definition.id,
      name: definition.name,
      amount: nutrient.amount,
      unit: definition.unit,
    });
  }
  return { ok: true, value: normalized };
}

function formatGrams(value: number) {
  return `${Number.isInteger(value) ? value : value.toFixed(1)}g`;
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}
